// apps/web/src/features/russia-requests/components/RussiaRequestDrawerTabs.tsx
//
// Russia drawer tab strip (#972, #1548). Uses the shared
// `WorkspaceTabStrip` primitive so the visual + interaction model stays
// identical to the China drawer. The lean drawer (#1548) carries exactly
// three sections — `cargo`, `cost`, `chat` — with these icon pairings:
//
//   cargo=package · cost=truck · chat=message-square
//
// @aiInvariant on narrow viewports tabs collapse to icon-plus-bare-count.
//   The `aria-label` keeps the desktop "<localized> (N)" wording so
//   screen-reader queries keep matching across breakpoints.

import { useTranslation } from 'react-i18next';
import type { RussiaRequest } from '@magimp/shared';
import {
  WorkspaceTabStrip,
  type IconName,
  type WorkspaceTabDescriptor,
} from '../../../ui/index.js';
import { entityKey, useNotificationsByEntity } from '../../notifications/index.js';
import { useRussiaRequestChatUnreadSummary } from '../queries.js';

// INVARIANT (#1067): `chat` is conditionally appended at render time when
//   the current actor passes `canActAsRussiaRequestChatParty`. The union
//   stays stable because every consumer that mounts a tab panel — the
//   drawer's body switch — already handles the `'chat'` branch behind
//   the same gate.
export const RUSSIA_DRAWER_TABS = ['cargo', 'cost', 'chat'] as const;
export type RussiaDrawerTabId = (typeof RUSSIA_DRAWER_TABS)[number];

const TAB_ICON_BY_ID: Record<RussiaDrawerTabId, IconName> = {
  cargo: 'package',
  cost: 'truck',
  chat: 'message-square',
};

interface Props {
  request: RussiaRequest;
  tab: RussiaDrawerTabId;
  setTab: (t: RussiaDrawerTabId) => void;
  narrow: boolean;
  // INVARIANT (#1067): the drawer computes the chat-party predicate and
  //   passes the result down. When false, the chat tab MUST be hidden
  //   and its unread-summary query MUST NOT mount so non-parties never
  //   surface chat metadata.
  canSeeChat: boolean;
}

export function RussiaRequestDrawerTabs({ request, tab, setTab, narrow, canSeeChat }: Props) {
  const { t } = useTranslation('russiaRequests');
  // WHY (#1067): the chat-tab count chip reads from the per-row unread
  //   summary so it updates live without a drawer refresh. The query is
  //   scoped to a single id here; realtime fan-out invalidates the
  //   `chatUnreadSummaryPrefix` slice so it stays current. Gated on
  //   `canSeeChat` so a non-party never hits the endpoint.
  const unreadSummaryQuery = useRussiaRequestChatUnreadSummary([request.id], {
    enabled: canSeeChat,
  });
  const unreadCount = canSeeChat ? (unreadSummaryQuery.data?.[0]?.unreadCount ?? 0) : 0;
  // INVARIANT: i18n keys stay literal so the strict-literal resource-bundle
  //   check verifies every tab id has a real translation entry. A
  //   `t(\`drawer.tabs.${id}\`)` template would widen the key to the union
  //   of every namespace value and trip the strict gate.
  const baseLabels: Record<RussiaDrawerTabId, string> = {
    cargo: t('drawer.tabs.cargo'),
    cost: t('drawer.tabs.cost'),
    chat: t('drawer.tabs.chat'),
  };
  // INVARIANT (#1072): RF tab strip shares the notifications query with
  //   row badges. Notification badges layer on top of the existing
  //   counts; they don't replace them.
  const { byEntity } = useNotificationsByEntity();
  const summary = byEntity.get(entityKey('RUSSIA_REQUEST', request.id));
  const counts: Record<RussiaDrawerTabId, number | null> = {
    cargo: null,
    cost: null,
    chat: unreadCount > 0 ? unreadCount : null,
  };
  // WHY (#1548): the lean drawer folds the source routing tabs onto three
  //   sections — `cargo` absorbs the info/items/boxes notification slots,
  //   `cost` absorbs `carriers`, and `audit` notifications drop from the
  //   strip (the audit tab is gone). The summary slots keep their original
  //   routing keys (a backend wire contract); only the fold changes.
  const tabNotifications: Record<RussiaDrawerTabId, number> = {
    cargo: (summary?.tabs.info ?? 0) + (summary?.tabs.items ?? 0) + (summary?.tabs.boxes ?? 0),
    cost: summary?.tabs.carriers ?? 0,
    chat: summary?.tabs.chat ?? 0,
  };
  // INVARIANT (#1067): the chat tab is conditionally rendered behind the
  //   `canSeeChat` predicate; non-parties never see it in the strip. The
  //   drawer body switch applies the same gate so a deep-link to
  //   `?tab=chat` falls back to `info` and the chat panel never mounts.
  const visibleTabs = RUSSIA_DRAWER_TABS.filter((id) => id !== 'chat' || canSeeChat);
  const tabs: WorkspaceTabDescriptor<RussiaDrawerTabId>[] = visibleTabs.map((id) => {
    const count = counts[id];
    const label = count === null ? baseLabels[id] : `${baseLabels[id]} (${count})`;
    return {
      id,
      label,
      icon: TAB_ICON_BY_ID[id],
      count,
      notificationCount: tabNotifications[id],
      controls: `russia-request-drawer-tabpanel-${id}`,
      buttonId: `russia-request-drawer-tab-${id}`,
      testId: `russia-request-drawer-tab-${id}`,
    };
  });
  return (
    <WorkspaceTabStrip
      tabs={tabs}
      activeId={tab}
      onSelect={setTab}
      narrow={narrow}
      padding="0 24px"
      testId="russia-request-drawer-tablist"
    />
  );
}
